import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid, deDE, GridColDef } from '@mui/x-data-grid';
import { PlayArrow } from '@mui/icons-material';

import {
  IArnoImportRun,
  useActivateArnoImportRun,
  useArnoImportRuns,
} from '../../hooks/reactQuerys/ArnoAdminQueries';
import { ChipColor, formatDateTime } from './arnoAdminLabels';
import { useAlert } from '../../context/AlertContext';
import { useConfirmation } from '../../context/ConfirmationContext';

/**
 * Importläufe des ArnoTop-Bestands (#1245).
 *
 * Der Import selbst wird auf dem Server angestoßen, nicht von hier — er liest
 * ein Verzeichnis, das nur der Server kennt. Diese Seite zeigt, was gelaufen
 * ist, und erlaubt das, was fachlich hierher gehört: einen anderen Lauf aktiv
 * schalten. Das ist auch der Weg zurück, falls ein Stand nicht taugt.
 */

const RUN_STATUS_COLOR: Record<string, ChipColor> = {
  ACTIVE: 'success',
  READY: 'info',
  VALIDATED: 'info',
  IMPORTING: 'warning',
  SUPERSEDED: 'default',
  FAILED: 'error',
};

export const ImportRunTab: React.FC = () => {
  const { showAlert } = useAlert();
  const { showConfirmation } = useConfirmation();
  const { importRuns, isLoadingImportRuns } = useArnoImportRuns();
  const { activateImportRun, isActivatingImportRun } =
    useActivateArnoImportRun();

  const activate = async (run: IArnoImportRun) => {
    const confirmed = await showConfirmation({
      title: `Lauf #${run.idArnoImportRun} aktivieren?`,
      message:
        'Ab sofort beantwortet jede Word-Sitzung aus diesem Stand. Menschliche Zuordnungen und aufgelöste Verweise bleiben unberührt — sie hängen nicht am Lauf.',
      confirmButtonText: 'Aktivieren',
      abortButtonText: 'Abbrechen',
    });
    if (!confirmed) {
      return;
    }
    try {
      await activateImportRun(run.idArnoImportRun);
      showAlert({
        severity: 'success',
        text: `Lauf #${run.idArnoImportRun} ist aktiv.`,
      });
    } catch (error) {
      showAlert({
        severity: 'error',
        text:
          error instanceof Error
            ? `Aktivieren fehlgeschlagen: ${error.message}`
            : 'Aktivieren fehlgeschlagen.',
      });
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'idArnoImportRun',
      headerName: 'Lauf',
      width: 90,
      renderCell: parameters => `#${parameters.row.idArnoImportRun}`,
    },
    {
      field: 'status',
      headerName: 'Stand',
      width: 140,
      renderCell: parameters => (
        <Chip
          size='small'
          color={RUN_STATUS_COLOR[parameters.row.status] ?? 'default'}
          label={parameters.row.status}
        />
      ),
    },
    {
      field: 'sourceLabel',
      headerName: 'Quelle',
      flex: 2,
      minWidth: 220,
      renderCell: parameters => {
        const row = parameters.row as IArnoImportRun;
        return (
          <Box>
            <Typography variant='body2' noWrap>
              {row.sourceLabel ?? '–'}
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              {row.fileCount ?? 0} Dateien
              {row.parserVersion ? ` · Parser ${row.parserVersion}` : ''}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'startedAt',
      headerName: 'Gestartet',
      width: 170,
      valueGetter: parameters =>
        formatDateTime((parameters.row as IArnoImportRun).startedAt),
    },
    {
      field: 'activatedAt',
      headerName: 'Aktiviert',
      width: 170,
      valueGetter: parameters =>
        formatDateTime((parameters.row as IArnoImportRun).activatedAt),
    },
    {
      field: 'actions',
      headerName: 'Aktion',
      width: 160,
      sortable: false,
      align: 'right',
      headerAlign: 'right',
      renderCell: parameters => {
        const row = parameters.row as IArnoImportRun;
        const activatable = ['READY', 'SUPERSEDED'].includes(row.status);
        return (
          <Tooltip
            title={
              activatable
                ? 'Diesen Stand aktiv schalten'
                : 'Nur ein geprüfter (READY) oder ein früher aktiver (SUPERSEDED) Lauf lässt sich aktivieren.'
            }
          >
            <span>
              <Button
                size='small'
                startIcon={<PlayArrow />}
                disabled={!activatable || isActivatingImportRun}
                onClick={() => activate(row)}
              >
                Aktivieren
              </Button>
            </span>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <Box>
      <Alert severity='info' sx={{ mb: 2 }}>
        Ein Import wird auf dem Server ausgeführt; das Exportverzeichnis ist
        dort konfiguriert. Nach dem Import braucht die Laufzeit ArnoTop nicht
        mehr.
      </Alert>

      <Stack direction='row' sx={{ mb: 1 }}>
        <Typography variant='body2' color='text.secondary'>
          Die letzten {importRuns.length} Läufe, neueste zuerst.
        </Typography>
      </Stack>

      <DataGrid
        autoHeight
        localeText={deDE.components.MuiDataGrid.defaultProps.localeText}
        rows={importRuns}
        columns={columns}
        getRowId={row => row.idArnoImportRun}
        loading={isLoadingImportRuns}
        disableRowSelectionOnClick
        initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
        pageSizeOptions={[25, 50]}
      />
    </Box>
  );
};
